/**
 * 笔画缓冲（M2-09 / DESIGN.md §5.2）。
 *
 * §5.2 的关键约定：**flow 在同一笔画内累积，opacity 是笔画整体上限。**
 * 这就是必须有独立缓冲的原因——直接往图层上画，自交叉区域会
 * 一遍遍叠加 opacity，重叠处越涂越深，这不是画笔而是印章。
 *
 * 缓冲只存**覆盖度 α ∈ [0,1]**（Float32，按块稀疏分配），每个 stamp 按
 * `α' = α + flow·c·(1−α)` 累积（c 是笔尖覆盖度），天然饱和于 1。
 * 收笔时一次性以 `α·opacity` 为 alpha 把颜色合进图层块——
 * 上限约束由乘法保证，自交叉再多也超不过 opacity。
 *
 * 硬边圆笔的覆盖度是解析的：内圈全覆盖，从 hardness·r 到 r 线性衰减，
 * 且至少保留 1px 过渡做抗锯齿（GPU 版 M2-10 用 SDF 做同一件事）。
 */
import { TILE, layerId as makeLayerId, tileKey, type LayerId, type TileKey } from '../tile/key';
import { BYTES_PER_TILE, type TileStore } from '../tile/store';
import { adjustHsl } from '../adjustments/lut';
import type { Stamp } from './sampler';

/** 减淡/加深工具的「范围」选项——跟真实 Photoshop 选项栏一致。 */
export type ToneRange = 'shadows' | 'midtones' | 'highlights';

/**
 * 阴影/中间调/高光的亮度权重：三段三角权重，对任意亮度之和恒为 1
 * （阴影在 L=0 权重 1、L≥0.5 归零；高光反过来；中间调在 L=0.5 权重 1、
 * 两端归零）。没有这个权重的话，减淡/加深不管画在画面哪个亮度区域强度
 * 都一样，跟真实 PS "选阴影只提亮暗部、高光基本不受影响"的手感不一样，
 * 也是"效果显得又平又假"这条反馈的根因。
 */
function toneRangeWeight(luma: number, range: ToneRange): number {
  const l = luma / 255;
  if (range === 'shadows') return Math.min(1, Math.max(0, 1 - 2 * l));
  if (range === 'highlights') return Math.min(1, Math.max(0, 2 * l - 1));
  return Math.min(1, Math.max(0, 1 - Math.abs(2 * l - 1)));
}

/**
 * 像素事务的最小面。应用层的 PixelTransaction（M1-08）结构性满足它；
 * 这里不 import app——分层是单向的，engine 只声明自己需要什么。
 */
export interface StrokeTransaction {
  touch(key: TileKey): void;
  capture(): void;
}

export interface BrushColor {
  /** sRGB 0–255。 */
  readonly r: number;
  readonly g: number;
  readonly b: number;
}

export class StrokeBuffer {
  /** 覆盖度块，键 "tx ty"。 */
  readonly #tiles = new Map<string, Float32Array>();
  readonly #docWidth: number;
  readonly #docHeight: number;
  readonly #hardness: number;

  constructor(docWidth: number, docHeight: number, hardness: number) {
    this.#docWidth = docWidth;
    this.#docHeight = docHeight;
    this.#hardness = Math.min(1, Math.max(0, hardness));
  }

  get touchedTiles(): number {
    return this.#tiles.size;
  }

  /** 读一点的累积覆盖度（测试与预览用）。 */
  alphaAt(x: number, y: number): number {
    const tile = this.#tiles.get(`${Math.floor(x / TILE)} ${Math.floor(y / TILE)}`);
    if (tile === undefined) return 0;
    return tile[(y % TILE) * TILE + (x % TILE)]!;
  }

  /** 把一个 stamp 累积进缓冲。 */
  stamp(stamp: Stamp): void {
    const radius = stamp.size / 2;
    if (radius <= 0 || stamp.flow <= 0) return;
    const inner = Math.min(radius * this.#hardness, radius - 1);

    const x0 = Math.max(0, Math.floor(stamp.x - radius));
    const y0 = Math.max(0, Math.floor(stamp.y - radius));
    const x1 = Math.min(this.#docWidth - 1, Math.ceil(stamp.x + radius));
    const y1 = Math.min(this.#docHeight - 1, Math.ceil(stamp.y + radius));

    for (let y = y0; y <= y1; y++) {
      for (let x = x0; x <= x1; x++) {
        // 像素中心到笔心的距离
        const d = Math.hypot(x + 0.5 - stamp.x, y + 0.5 - stamp.y);
        let coverage: number;
        if (d <= inner) coverage = 1;
        else if (d >= radius) coverage = 0;
        else coverage = (radius - d) / (radius - inner);
        if (coverage <= 0) continue;

        const id = `${Math.floor(x / TILE)} ${Math.floor(y / TILE)}`;
        let tile = this.#tiles.get(id);
        if (tile === undefined) {
          tile = new Float32Array(TILE * TILE);
          this.#tiles.set(id, tile);
        }
        const at = (y % TILE) * TILE + (x % TILE);
        const current = tile[at]!;
        // flow 累积：饱和于 1，这是「上限为 opacity」的前一半
        tile[at] = current + stamp.flow * coverage * (1 - current);
      }
    }
  }

  /**
   * 把整段笔画一次性合并进图层。
   *
   * `transaction.touch` 必须发生在写入**之前**（M1-08：快照拍的是改前内容），
   * 然后 `capture()` 再写。合并语义是 source-over，源 alpha = α·opacity。
   * 橡皮模式（erase）改为把目标 alpha 乘以 (1−α·opacity)。
   */
  mergeInto(
    store: TileStore,
    layer: LayerId | string,
    color: BrushColor,
    opacity: number,
    transaction: StrokeTransaction,
    mode: 'paint' | 'erase' = 'paint',
  ): void {
    const target = typeof layer === 'string' ? makeLayerId(layer) : layer;
    // 先声明全部将被改动的块，再统一拍快照，最后写入
    const keys = [...this.#tiles.keys()].map((id) => {
      const [tx, ty] = id.split(' ').map(Number) as [number, number];
      return { id, key: tileKey(target, tx, ty) };
    });
    for (const { key } of keys) transaction.touch(key);
    transaction.capture();

    for (const { id, key } of keys) {
      const coverageTile = this.#tiles.get(id)!;
      const existing = store.get(key)?.data;
      const out = new Uint8Array(BYTES_PER_TILE);
      if (existing !== null && existing !== undefined) out.set(existing);

      for (let i = 0; i < TILE * TILE; i++) {
        const alpha = coverageTile[i]! * opacity;
        if (alpha <= 0) continue;
        const at = i * 4;
        if (mode === 'erase') {
          out[at + 3] = Math.round(out[at + 3]! * (1 - alpha));
          continue;
        }
        // 直通 alpha 的 source-over
        const dstA = out[at + 3]! / 255;
        const outA = alpha + dstA * (1 - alpha);
        if (outA > 0) {
          out[at] = Math.round((color.r * alpha + out[at]! * dstA * (1 - alpha)) / outA);
          out[at + 1] = Math.round((color.g * alpha + out[at + 1]! * dstA * (1 - alpha)) / outA);
          out[at + 2] = Math.round((color.b * alpha + out[at + 2]! * dstA * (1 - alpha)) / outA);
        }
        out[at + 3] = Math.round(outA * 255);
      }
      store.set(key, out);
    }
  }

  /**
   * 仿制合并（M3-12）：目标像素的颜色来自**同一图层**在
   * (x+offsetX, y+offsetY) 处的起笔时内容，按覆盖度 source-over。
   *
   * 源在写入前整体冻结（抓住旧数组引用）：笔画与其源区域重叠时
   * 克隆的是起笔时的画面，而不是边画边采的递归结果。
   * 源越界/透明处贡献为零（等于没画）。
   */
  mergeCloneInto(
    store: TileStore,
    layer: LayerId | string,
    offsetX: number,
    offsetY: number,
    opacity: number,
    transaction: StrokeTransaction,
  ): void {
    const target = typeof layer === 'string' ? makeLayerId(layer) : layer;
    const keys = [...this.#tiles.keys()].map((id) => {
      const [tx, ty] = id.split(' ').map(Number) as [number, number];
      return { id, tx, ty, key: tileKey(target, tx, ty) };
    });
    for (const { key } of keys) transaction.touch(key);
    transaction.capture();

    // 冻结源：先抓所有可能被读的源块引用，再开始写
    const frozen = new Map<string, Uint8Array | null>();
    const freeze = (tx: number, ty: number): void => {
      const id = `${tx} ${ty}`;
      if (!frozen.has(id)) frozen.set(id, store.get(tileKey(target, tx, ty))?.data ?? null);
    };
    for (const { tx, ty } of keys) {
      // 负偏移会落到画布外：块坐标非负是硬约束，源越界由 sourceAt 兜底
      const sx0 = Math.max(0, Math.floor((tx * TILE + offsetX) / TILE));
      const sy0 = Math.max(0, Math.floor((ty * TILE + offsetY) / TILE));
      const sx1 = Math.floor((tx * TILE + TILE - 1 + offsetX) / TILE);
      const sy1 = Math.floor((ty * TILE + TILE - 1 + offsetY) / TILE);
      for (let sy = sy0; sy <= sy1; sy++) {
        for (let sx = sx0; sx <= sx1; sx++) freeze(sx, sy);
      }
    }
    const sourceAt = (x: number, y: number): [number, number, number, number] => {
      // 取整：像素存储只认整数下标，非整数坐标下 `data[at]` 会静默命中
      // undefined（不是越界抛错），后续 alpha 计算里 undefined/255 = NaN，
      // 写回 Uint8Array 时 NaN 被强转成 0——看起来就是"整片被擦成透明"，
      // 且颜色通道因为 `outA > 0` 判断也是 false 而保持原样，酷似橡皮擦。
      // offsetX/offsetY 来自非 100% 缩放下换算出的文档坐标，本来就可能带小数，
      // 这里兜底取整（而不是要求调用方保证整数）。
      const ix = Math.floor(x);
      const iy = Math.floor(y);
      if (ix < 0 || iy < 0 || ix >= this.#docWidth || iy >= this.#docHeight) return [0, 0, 0, 0];
      const data = frozen.get(`${Math.floor(ix / TILE)} ${Math.floor(iy / TILE)}`);
      if (data == null) return [0, 0, 0, 0];
      const at = ((iy % TILE) * TILE + (ix % TILE)) * 4;
      return [data[at]!, data[at + 1]!, data[at + 2]!, data[at + 3]!];
    };

    for (const { id, tx, ty, key } of keys) {
      const coverageTile = this.#tiles.get(id)!;
      const existing = store.get(key)?.data;
      const out = new Uint8Array(BYTES_PER_TILE);
      if (existing !== null && existing !== undefined) out.set(existing);

      for (let i = 0; i < TILE * TILE; i++) {
        const coverage = coverageTile[i]!;
        if (coverage <= 0) continue;
        const x = tx * TILE + (i % TILE);
        const y = ty * TILE + Math.floor(i / TILE);
        const [r, g, b, a] = sourceAt(x + offsetX, y + offsetY);
        const alpha = coverage * opacity * (a / 255);
        if (alpha <= 0) continue;
        const at = i * 4;
        const dstA = out[at + 3]! / 255;
        const outA = alpha + dstA * (1 - alpha);
        if (outA > 0) {
          out[at] = Math.round((r * alpha + out[at]! * dstA * (1 - alpha)) / outA);
          out[at + 1] = Math.round((g * alpha + out[at + 1]! * dstA * (1 - alpha)) / outA);
          out[at + 2] = Math.round((b * alpha + out[at + 2]! * dstA * (1 - alpha)) / outA);
        }
        out[at + 3] = Math.round(outA * 255);
      }
      store.set(key, out);
    }
  }

  /**
   * 修复画笔合并（补齐 registry.ts 早先登记的占位工具）：跟仿制图章
   * （mergeCloneInto）用同一套采样/对齐机制——区别只在多了一步色彩匹配。
   *
   * 真正的"无缝愈合"是梯度域泊松融合（Poisson blending，解一个稀疏线性
   * 方程组），这套算法储备里没有（DESIGN.md 附录 A 没列）。这里做的是
   * 便宜得多但确实有效的近似：笔画覆盖的目的地区域与取样的源区域，
   * 各自算一个加权平均色，取样颜色整体加上"目的地均值 − 源均值"的偏移
   * 再合成——相当于把源patch的色调整体拉到跟目的地一致，色调/明暗差异
   * 明显时比纯克隆自然，但不解决边缘处的精细过渡（那是泊松融合才管的事）。
   */
  mergeHealingCloneInto(
    store: TileStore,
    layer: LayerId | string,
    offsetX: number,
    offsetY: number,
    opacity: number,
    transaction: StrokeTransaction,
  ): void {
    const target = typeof layer === 'string' ? makeLayerId(layer) : layer;
    const keys = [...this.#tiles.keys()].map((id) => {
      const [tx, ty] = id.split(' ').map(Number) as [number, number];
      return { id, tx, ty, key: tileKey(target, tx, ty) };
    });
    for (const { key } of keys) transaction.touch(key);
    transaction.capture();

    const frozen = new Map<string, Uint8Array | null>();
    const freeze = (tx: number, ty: number): void => {
      const id = `${tx} ${ty}`;
      if (!frozen.has(id)) frozen.set(id, store.get(tileKey(target, tx, ty))?.data ?? null);
    };
    for (const { tx, ty } of keys) {
      const sx0 = Math.max(0, Math.floor((tx * TILE + offsetX) / TILE));
      const sy0 = Math.max(0, Math.floor((ty * TILE + offsetY) / TILE));
      const sx1 = Math.floor((tx * TILE + TILE - 1 + offsetX) / TILE);
      const sy1 = Math.floor((ty * TILE + TILE - 1 + offsetY) / TILE);
      for (let sy = sy0; sy <= sy1; sy++) {
        for (let sx = sx0; sx <= sx1; sx++) freeze(sx, sy);
      }
    }
    const sourceAt = (x: number, y: number): [number, number, number, number] => {
      // 取整：像素存储只认整数下标，非整数坐标下 `data[at]` 会静默命中
      // undefined（不是越界抛错），后续 alpha 计算里 undefined/255 = NaN，
      // 写回 Uint8Array 时 NaN 被强转成 0——看起来就是"整片被擦成透明"，
      // 且颜色通道因为 `outA > 0` 判断也是 false 而保持原样，酷似橡皮擦。
      // offsetX/offsetY 来自非 100% 缩放下换算出的文档坐标，本来就可能带小数，
      // 这里兜底取整（而不是要求调用方保证整数）。
      const ix = Math.floor(x);
      const iy = Math.floor(y);
      if (ix < 0 || iy < 0 || ix >= this.#docWidth || iy >= this.#docHeight) return [0, 0, 0, 0];
      const data = frozen.get(`${Math.floor(ix / TILE)} ${Math.floor(iy / TILE)}`);
      if (data == null) return [0, 0, 0, 0];
      const at = ((iy % TILE) * TILE + (ix % TILE)) * 4;
      return [data[at]!, data[at + 1]!, data[at + 2]!, data[at + 3]!];
    };
    // 目标读现存图层数据（跟源不同，允许在同一笔画内正常读写）
    const destAt = (tx: number, ty: number, i: number): [number, number, number, number] | null => {
      const existing = store.get(tileKey(target, tx, ty))?.data;
      if (existing == null) return null;
      const at = i * 4;
      return existing[at + 3]! > 0 ? [existing[at]!, existing[at + 1]!, existing[at + 2]!, existing[at + 3]!] : null;
    };

    // 第一遍：按覆盖度加权，分别算源 patch 与目的地现状的均色
    let srcSumR = 0, srcSumG = 0, srcSumB = 0;
    let dstSumR = 0, dstSumG = 0, dstSumB = 0;
    let weightSum = 0;
    for (const { id, tx, ty } of keys) {
      const coverageTile = this.#tiles.get(id)!;
      for (let i = 0; i < TILE * TILE; i++) {
        const coverage = coverageTile[i]!;
        if (coverage <= 0) continue;
        const x = tx * TILE + (i % TILE);
        const y = ty * TILE + Math.floor(i / TILE);
        const [sr, sg, sb, sa] = sourceAt(x + offsetX, y + offsetY);
        if (sa === 0) continue;
        const dest = destAt(tx, ty, i);
        if (dest === null) continue;
        srcSumR += sr * coverage;
        srcSumG += sg * coverage;
        srcSumB += sb * coverage;
        dstSumR += dest[0] * coverage;
        dstSumG += dest[1] * coverage;
        dstSumB += dest[2] * coverage;
        weightSum += coverage;
      }
    }
    const correction =
      weightSum > 0
        ? {
            r: (dstSumR - srcSumR) / weightSum,
            g: (dstSumG - srcSumG) / weightSum,
            b: (dstSumB - srcSumB) / weightSum,
          }
        : { r: 0, g: 0, b: 0 };
    const clamp = (v: number): number => Math.min(255, Math.max(0, Math.round(v)));

    for (const { id, tx, ty, key } of keys) {
      const coverageTile = this.#tiles.get(id)!;
      const existing = store.get(key)?.data;
      const out = new Uint8Array(BYTES_PER_TILE);
      if (existing !== null && existing !== undefined) out.set(existing);

      for (let i = 0; i < TILE * TILE; i++) {
        const coverage = coverageTile[i]!;
        if (coverage <= 0) continue;
        const x = tx * TILE + (i % TILE);
        const y = ty * TILE + Math.floor(i / TILE);
        const [r, g, b, a] = sourceAt(x + offsetX, y + offsetY);
        const alpha = coverage * opacity * (a / 255);
        if (alpha <= 0) continue;
        const at = i * 4;
        const dstA = out[at + 3]! / 255;
        const outA = alpha + dstA * (1 - alpha);
        const cr = clamp(r + correction.r);
        const cg = clamp(g + correction.g);
        const cb = clamp(b + correction.b);
        if (outA > 0) {
          out[at] = Math.round((cr * alpha + out[at]! * dstA * (1 - alpha)) / outA);
          out[at + 1] = Math.round((cg * alpha + out[at + 1]! * dstA * (1 - alpha)) / outA);
          out[at + 2] = Math.round((cb * alpha + out[at + 2]! * dstA * (1 - alpha)) / outA);
        }
        out[at + 3] = Math.round(outA * 255);
      }
      store.set(key, out);
    }
  }

  /**
   * 减淡合并：不是往图层里 source-over 一个常量色，而是把**已有像素**朝
   * 白色推——`out = v + (255-v)·effective`，`effective = alpha·rangeWeight`。
   * `alpha` 是这一笔在该点累积的覆盖度（含 flow 累积、外部已乘曝光度），
   * `rangeWeight` 按该像素当前亮度算出（`toneRangeWeight`），跟真实 PS 一致：
   * 选"阴影"时高光基本不受影响，选"高光"时阴影基本不受影响，不是不分
   * 青红皂白整块提亮。只对已有内容生效：一个块从未画过（`existing == null`）
   * 时跳过，不能无中生有地把透明区域"减淡"成不透明的灰白色块。alpha
   * 通道不变——减淡是调色不是调透明度。
   */
  mergeDodgeInto(
    store: TileStore,
    layer: LayerId | string,
    transaction: StrokeTransaction,
    range: ToneRange,
  ): void {
    const target = typeof layer === 'string' ? makeLayerId(layer) : layer;
    const keys = [...this.#tiles.keys()].map((id) => {
      const [tx, ty] = id.split(' ').map(Number) as [number, number];
      return { id, key: tileKey(target, tx, ty) };
    });
    for (const { key } of keys) transaction.touch(key);
    transaction.capture();

    for (const { id, key } of keys) {
      const coverageTile = this.#tiles.get(id)!;
      const existing = store.get(key)?.data;
      if (existing == null) continue; // 没画过的块没有像素可减淡
      const out = new Uint8Array(BYTES_PER_TILE);
      out.set(existing);

      for (let i = 0; i < TILE * TILE; i++) {
        const alpha = coverageTile[i]!;
        if (alpha <= 0) continue;
        const at = i * 4;
        if (out[at + 3] === 0) continue; // 透明像素没有颜色可减淡
        const luma = 0.299 * out[at]! + 0.587 * out[at + 1]! + 0.114 * out[at + 2]!;
        const effective = alpha * toneRangeWeight(luma, range);
        if (effective <= 0) continue;
        for (let c = 0; c < 3; c++) {
          const v = out[at + c]!;
          out[at + c] = Math.round(v + (255 - v) * effective);
        }
      }
      store.set(key, out);
    }
  }

  /**
   * 加深合并：减淡的镜像——把已有像素朝黑色推，
   * `out = v·(1−effective)`。跟减淡同一套约束与范围权重逻辑。
   */
  mergeBurnInto(
    store: TileStore,
    layer: LayerId | string,
    transaction: StrokeTransaction,
    range: ToneRange,
  ): void {
    const target = typeof layer === 'string' ? makeLayerId(layer) : layer;
    const keys = [...this.#tiles.keys()].map((id) => {
      const [tx, ty] = id.split(' ').map(Number) as [number, number];
      return { id, key: tileKey(target, tx, ty) };
    });
    for (const { key } of keys) transaction.touch(key);
    transaction.capture();

    for (const { id, key } of keys) {
      const coverageTile = this.#tiles.get(id)!;
      const existing = store.get(key)?.data;
      if (existing == null) continue; // 没画过的块没有像素可加深
      const out = new Uint8Array(BYTES_PER_TILE);
      out.set(existing);

      for (let i = 0; i < TILE * TILE; i++) {
        const alpha = coverageTile[i]!;
        if (alpha <= 0) continue;
        const at = i * 4;
        if (out[at + 3] === 0) continue; // 透明像素没有颜色可加深
        const luma = 0.299 * out[at]! + 0.587 * out[at + 1]! + 0.114 * out[at + 2]!;
        const effective = alpha * toneRangeWeight(luma, range);
        if (effective <= 0) continue;
        for (let c = 0; c < 3; c++) {
          const v = out[at + c]!;
          out[at + c] = Math.round(v * (1 - effective));
        }
      }
      store.set(key, out);
    }
  }

  /**
   * 海绵合并（补齐 registry.ts 早先登记的占位工具）：调饱和度，不调颜色/明度——
   * 复用调整图层同一套 HSL 公式（engine/adjustments/lut.ts 的 `adjustHsl`），
   * 只是逐像素喂不同的饱和度增量（该点这一笔的累积覆盖度乘 ±100）。
   * `direction` 决定符号：'saturate' 越涂越艳，'desaturate' 越涂越灰
   * （Photoshop 的海绵工具本身就是这两种模式的开关，不是强度正负号）。
   */
  mergeSaturateInto(
    store: TileStore,
    layer: LayerId | string,
    transaction: StrokeTransaction,
    direction: 'saturate' | 'desaturate',
  ): void {
    const target = typeof layer === 'string' ? makeLayerId(layer) : layer;
    const sign = direction === 'saturate' ? 1 : -1;
    const keys = [...this.#tiles.keys()].map((id) => {
      const [tx, ty] = id.split(' ').map(Number) as [number, number];
      return { id, key: tileKey(target, tx, ty) };
    });
    for (const { key } of keys) transaction.touch(key);
    transaction.capture();

    for (const { id, key } of keys) {
      const coverageTile = this.#tiles.get(id)!;
      const existing = store.get(key)?.data;
      if (existing == null) continue; // 没画过的块没有像素可调饱和度
      const out = new Uint8Array(BYTES_PER_TILE);
      out.set(existing);

      for (let i = 0; i < TILE * TILE; i++) {
        const alpha = coverageTile[i]!;
        if (alpha <= 0) continue;
        const at = i * 4;
        if (out[at + 3] === 0) continue; // 透明像素没有颜色可调
        const [r, g, b] = adjustHsl(out[at]!, out[at + 1]!, out[at + 2]!, {
          hue: 0,
          saturation: sign * alpha * 100,
          lightness: 0,
        });
        out[at] = r;
        out[at + 1] = g;
        out[at + 2] = b;
      }
      store.set(key, out);
    }
  }

  /** 读一个文档空间像素的 RGB（跨块，取整）；块不存在或越界返回 null。 */
  #readLayerPixel(
    store: TileStore,
    target: LayerId,
    x: number,
    y: number,
  ): readonly [number, number, number, number] | null {
    if (x < 0 || y < 0 || x >= this.#docWidth || y >= this.#docHeight) return null;
    const tx = Math.floor(x / TILE);
    const ty = Math.floor(y / TILE);
    const data = store.get(tileKey(target, tx, ty))?.data;
    if (data == null) return null;
    const at = ((y % TILE) * TILE + (x % TILE)) * 4;
    return [data[at]!, data[at + 1]!, data[at + 2]!, data[at + 3]!];
  }

  /**
   * 模糊/锐化合并（对比 Photoshop 补全第一档缺口 阶段 4）：跟 `mergeDodgeInto`
   * 同一形状——只对已有像素生效（`existing == null` 跳过，无中生有的透明区域
   * 没有内容可模糊/锐化），`effective` 直接用累积覆盖度（含 flow 累积，跟
   * 减淡/加深同一惯例，不额外接 opacity 参数）。
   *
   * 模糊：往 3×3 邻域盒式模糊结果靠一步，`sharpen=false`；锐化：往"原值 −
   * 邻域均值"的反方向推一步（USM 同一直觉），`sharpen=true`。邻域读取跨块
   * （`#readLayerPixel`），越界/未分配的邻居当作跟中心同色（不把画布边缘
   * 悄悄拉暗，也不用取样画布外的透明当作邻居把边缘颜色冲淡）。
   */
  #mergeToneNeighborhood(
    store: TileStore,
    layer: LayerId | string,
    transaction: StrokeTransaction,
    sharpen: boolean,
  ): void {
    const target = typeof layer === 'string' ? makeLayerId(layer) : layer;
    const keys = [...this.#tiles.keys()].map((id) => {
      const [tx, ty] = id.split(' ').map(Number) as [number, number];
      return { id, tx, ty, key: tileKey(target, tx, ty) };
    });
    for (const { key } of keys) transaction.touch(key);
    transaction.capture();

    for (const { id, tx, ty, key } of keys) {
      const coverageTile = this.#tiles.get(id)!;
      const existing = store.get(key)?.data;
      if (existing == null) continue;
      const out = new Uint8Array(BYTES_PER_TILE);
      out.set(existing);

      for (let i = 0; i < TILE * TILE; i++) {
        const alpha = coverageTile[i]!;
        if (alpha <= 0) continue;
        const at = i * 4;
        if (out[at + 3] === 0) continue; // 透明像素没有颜色可模糊/锐化
        const x = tx * TILE + (i % TILE);
        const y = ty * TILE + Math.floor(i / TILE);
        for (let c = 0; c < 3; c++) {
          let sum = 0;
          let count = 0;
          for (let ny = -1; ny <= 1; ny++) {
            for (let nx = -1; nx <= 1; nx++) {
              const neighbor = this.#readLayerPixel(store, target, x + nx, y + ny);
              sum += neighbor === null ? out[at + c]! : neighbor[c]!;
              count++;
            }
          }
          const mean = sum / count;
          const v = out[at + c]!;
          const goal = sharpen ? v + (v - mean) : mean;
          out[at + c] = Math.round(Math.min(255, Math.max(0, v + (goal - v) * alpha)));
        }
      }
      store.set(key, out);
    }
  }

  /** 模糊画笔：往局部邻域均值靠拢。 */
  mergeBlurInto(store: TileStore, layer: LayerId | string, transaction: StrokeTransaction): void {
    this.#mergeToneNeighborhood(store, layer, transaction, false);
  }

  /** 锐化画笔：局部对比度反向增强（USM 同一直觉）。 */
  mergeSharpenInto(store: TileStore, layer: LayerId | string, transaction: StrokeTransaction): void {
    this.#mergeToneNeighborhood(store, layer, transaction, true);
  }

  /**
   * 涂抹画笔（简化近似，对比 Photoshop 补全第一档缺口 阶段 4）：跟仿制图章
   * （`mergeCloneInto`）同一套冻结取样机制，但不是 source-over 替换——只把
   * 现有颜色往"偏移 (offsetX, offsetY) 处起笔时的颜色"按覆盖度线性混合，
   * alpha 完全不变（涂抹工具是拖动已有颜料，不是盖新内容上去）。
   *
   * 真正的涂抹是逐 stamp、顺序相关的"越涂越拖"效果——每一笔都从上一笔
   * 已经被拖过的结果继续拖，这需要打破 `StrokeBuffer`"一笔画只在收笔时
   * 合并一次"的既有假设（M2-09 起的架构约定），属于更大的工程，这次不做，
   * 已在完成报告披露。这里是"整笔画统一拖一个方向、拖多少由覆盖度决定"
   * 的静态近似。
   */
  mergeSmudgeInto(
    store: TileStore,
    layer: LayerId | string,
    offsetX: number,
    offsetY: number,
    transaction: StrokeTransaction,
  ): void {
    const target = typeof layer === 'string' ? makeLayerId(layer) : layer;
    const keys = [...this.#tiles.keys()].map((id) => {
      const [tx, ty] = id.split(' ').map(Number) as [number, number];
      return { id, tx, ty, key: tileKey(target, tx, ty) };
    });
    for (const { key } of keys) transaction.touch(key);
    transaction.capture();

    const frozen = new Map<string, Uint8Array | null>();
    const freeze = (tx: number, ty: number): void => {
      const id = `${tx} ${ty}`;
      if (!frozen.has(id)) frozen.set(id, store.get(tileKey(target, tx, ty))?.data ?? null);
    };
    for (const { tx, ty } of keys) {
      const sx0 = Math.max(0, Math.floor((tx * TILE + offsetX) / TILE));
      const sy0 = Math.max(0, Math.floor((ty * TILE + offsetY) / TILE));
      const sx1 = Math.floor((tx * TILE + TILE - 1 + offsetX) / TILE);
      const sy1 = Math.floor((ty * TILE + TILE - 1 + offsetY) / TILE);
      for (let sy = sy0; sy <= sy1; sy++) {
        for (let sx = sx0; sx <= sx1; sx++) freeze(sx, sy);
      }
    }
    const sourceAt = (x: number, y: number): readonly [number, number, number, number] => {
      const ix = Math.floor(x);
      const iy = Math.floor(y);
      if (ix < 0 || iy < 0 || ix >= this.#docWidth || iy >= this.#docHeight) return [0, 0, 0, 0];
      const data = frozen.get(`${Math.floor(ix / TILE)} ${Math.floor(iy / TILE)}`);
      if (data == null) return [0, 0, 0, 0];
      const at = ((iy % TILE) * TILE + (ix % TILE)) * 4;
      return [data[at]!, data[at + 1]!, data[at + 2]!, data[at + 3]!];
    };

    for (const { id, tx, ty, key } of keys) {
      const coverageTile = this.#tiles.get(id)!;
      const existing = store.get(key)?.data;
      if (existing == null) continue; // 没画过的块没有颜色可拖
      const out = new Uint8Array(BYTES_PER_TILE);
      out.set(existing);

      for (let i = 0; i < TILE * TILE; i++) {
        const coverage = coverageTile[i]!;
        if (coverage <= 0) continue;
        const at = i * 4;
        if (out[at + 3] === 0) continue; // 透明像素没有颜色可拖
        const x = tx * TILE + (i % TILE);
        const y = ty * TILE + Math.floor(i / TILE);
        const [sr, sg, sb, sa] = sourceAt(x + offsetX, y + offsetY);
        if (sa === 0) continue; // 取样点没有颜色，拖不动
        out[at] = Math.round(out[at]! + (sr - out[at]!) * coverage);
        out[at + 1] = Math.round(out[at + 1]! + (sg - out[at + 1]!) * coverage);
        out[at + 2] = Math.round(out[at + 2]! + (sb - out[at + 2]!) * coverage);
        // alpha 不变：涂抹不新增/减少不透明度，只挪颜色
      }
      store.set(key, out);
    }
  }

  /**
   * 用选区蒙版裁剪覆盖度（逐像素乘 mask/255）。
   * 在 merge 之前调用一次——「选区外画不上」的实现就在这一步。
   */
  clipToMask(mask: {
    valueAt(x: number, y: number): number;
  }): void {
    for (const [id, tile] of this.#tiles) {
      const [tx, ty] = id.split(' ').map(Number) as [number, number];
      for (let y = 0; y < TILE; y++) {
        const docY = ty * TILE + y;
        if (docY >= this.#docHeight) break;
        for (let x = 0; x < TILE; x++) {
          const at = y * TILE + x;
          if (tile[at] === 0) continue;
          tile[at] = (tile[at]! * mask.valueAt(tx * TILE + x, docY)) / 255;
        }
      }
    }
  }

  /** 受影响的文档块坐标（渲染桥按块标脏用）。 */
  *tileCoords(): Generator<{ tx: number; ty: number }> {
    for (const id of this.#tiles.keys()) {
      const [tx, ty] = id.split(' ').map(Number) as [number, number];
      yield { tx, ty };
    }
  }
}
